/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import './AdminUsers.css';
import './Assignments.css';
import { apiFetch } from './api';
import CourseDropdown from './CourseDropdown';
import { useActiveCourse } from './ActiveCourseContext';


const EXAM_TYPE_LABELS = {
  'odev': 'Ödev', 'Odev': 'Ödev', 'Ödev': 'Ödev',
  'final': 'Dönem Sonu Sınavı', 'Final': 'Dönem Sonu Sınavı',
  'quiz': 'Kısa Sınav', 'Quiz': 'Kısa Sınav',
  'vize': 'Ara Sınav', 'Vize': 'Ara Sınav', 'Ara Sınav': 'Ara Sınav',
  'butunleme': 'Bütünleme', 'bütünleme': 'Bütünleme', 'Butunleme': 'Bütünleme', 'Bütünleme': 'Bütünleme',
  'Ontest': 'Ön Test', 'ontest': 'Ön Test',
};
const getExamTypeLabel = (type) => EXAM_TYPE_LABELS[type] || type || '';

const formatDeadline = (due_at) =>
  new Date(due_at).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const isPastDeadline = (due_at) => due_at && new Date(due_at) < new Date();

function Assignments({ userType }) {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [error, setError] = useState('');
  const activeCourseContext = useActiveCourse();

  // Modal (akademisyen ödev yükleme)
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDueAt, setModalDueAt] = useState('');
  const [modalFile, setModalFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [modalError, setModalError] = useState('');
  const modalFileRef = useRef();

  // Öğrenci gönderimleri (akademisyen görünümü)
  const [submissions, setSubmissions] = useState({});
  const [loadingSubmissions, setLoadingSubmissions] = useState({});
  const [expandedSubmissions, setExpandedSubmissions] = useState({});

  // Öğrenci ödev gönderme
  const [studentFile, setStudentFile] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [submitError, setSubmitError] = useState({});
  const studentFileRefs = useRef({});

  // Not girişi paneli
  const [gradeView, setGradeView] = useState({});
  const [gradeType, setGradeType] = useState({});         // { [assignmentId]: exam_weight_id (number) }
  const [examWeights, setExamWeights] = useState({});     // { [assignmentId]: [{id, exam_type, weight}] }
  const [deadlinePassed, setDeadlinePassed] = useState({}); // { [assignmentId]: bool }
  const [gradeStudents, setGradeStudents] = useState({});
  const [loadingGrades, setLoadingGrades] = useState({});
  const [gradeInputs, setGradeInputs] = useState({});
  const [savingGrades, setSavingGrades] = useState({});
  const [gradeError, setGradeError] = useState({});
  const [gradeSuccess, setGradeSuccess] = useState({});
  const [gradeExcelFile, setGradeExcelFile] = useState({});
  const [uploadingGradeExcel, setUploadingGradeExcel] = useState({});
  const gradeExcelRefs = useRef({});

  const isInstructor = ['instructor', 'program_baskan', 'rektor', 'rektor_yardimcisi'].includes(userType);

  useEffect(() => {
    fetchCourses();
    if (!isInstructor) fetchAssignments();
    else fetchAllInstructorAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      setError('');
      const endpoint = isInstructor ? '/instructor/courses' : '/student/courses';
      const res = await apiFetch(endpoint);
      const data = await res.json();
      if (data.success) setCourses(data.courses || []);
      else setError(data.message || 'Dersler yüklenemedi');
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingCourses(false);
    }
  };

  const programs = useMemo(() => {
    if (!isInstructor) return [];
    const seen = new Set();
    const list = [];
    courses.forEach((c) => {
      const p = c.program_name || '';
      if (p && !seen.has(p)) { seen.add(p); list.push(p); }
    });
    return list.sort((a, b) => a.localeCompare(b, 'tr'));
  }, [courses, isInstructor]);

  const filteredCourses = useMemo(() => {
    if (!isInstructor) return courses;
    if (!selectedProgram) return [];
    return courses.filter((c) => (c.program_name || '') === selectedProgram);
  }, [courses, selectedProgram, isInstructor]);

  const sortByCreatedAt = (list) =>
    [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const fetchAllInstructorAssignments = async () => {
    try {
      setLoadingAssignments(true);
      setError('');
      const res = await apiFetch('/instructor/assignments/all');
      const data = await res.json();
      if (data.success) setAssignments(sortByCreatedAt(data.assignments || []));
      else setError(data.message || 'Ödevler yüklenemedi');
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const fetchAssignments = async (courseId) => {
    try {
      setLoadingAssignments(true);
      setError('');
      setAssignments([]);
      const endpoint = isInstructor
        ? `/instructor/assignments?course_id=${courseId}`
        : courseId
          ? `/student/assignments?course_id=${courseId}`
          : `/student/assignments`;
      const res = await apiFetch(endpoint);
      const data = await res.json();
      if (data.success) setAssignments(sortByCreatedAt(data.assignments || []));
      else setError(data.message || 'Ödevler yüklenemedi');
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleProgramChange = (program) => {
    setSelectedProgram(program);
    setSelectedCourseId('');
    setAssignments([]);
    setError('');
  };

  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
    setError('');
    if (isInstructor) {
      if (courseId) fetchAssignments(courseId);
      else fetchAllInstructorAssignments();
    } else {
      fetchAssignments(courseId || undefined);
    }
    if (activeCourseContext) activeCourseContext.setActiveCourseId(courseId);
  };

  useEffect(() => {
    if (!activeCourseContext) return;
    const nextCourseId = activeCourseContext.activeCourseId;
    if (String(selectedCourseId) === String(nextCourseId)) return;
    setSelectedCourseId(nextCourseId);
    setError('');
    if (nextCourseId) fetchAssignments(nextCourseId);
    else fetchAllInstructorAssignments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseContext?.activeCourseId, userType]);

  // Akademisyen: ödev yükleme modal
  const openModal = () => {
    setModalTitle('');
    setModalDueAt('');
    setModalFile(null);
    setModalError('');
    setShowModal(true);
  };

  const handleModalUpload = async () => {
    if (!modalTitle.trim()) { setModalError('Lütfen bir başlık girin.'); return; }
    if (!modalFile) { setModalError('Lütfen bir dosya seçin.'); return; }
    setUploading(true);
    setModalError('');
    try {
      const formData = new FormData();
      formData.append('course_id', selectedCourseId);
      formData.append('title', modalTitle.trim());
      formData.append('due_at', modalDueAt);
      formData.append('file', modalFile);
      const res = await apiFetch('/instructor/assignments', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setShowModal(false);
        if (selectedCourseId) fetchAssignments(selectedCourseId);
        else fetchAllInstructorAssignments();
      } else {
        setModalError(data.message || 'Yükleme başarısız');
      }
    } catch {
      setModalError('Bağlantı hatası');
    } finally {
      setUploading(false);
    }
  };

  // Akademisyen: öğrenci gönderimlerini aç/kapat — her açılışta taze veri çeker
  const toggleSubmissions = async (assignmentId) => {
    const nowExpanded = !expandedSubmissions[assignmentId];
    setExpandedSubmissions((prev) => ({ ...prev, [assignmentId]: nowExpanded }));
    if (nowExpanded) {
      setLoadingSubmissions((prev) => ({ ...prev, [assignmentId]: true }));
      try {
        const res = await apiFetch(`/instructor/assignments/${assignmentId}/submissions`);
        const data = await res.json();
        if (data.success) setSubmissions((prev) => ({ ...prev, [assignmentId]: data.submissions || [] }));
      } catch { /* sessiz hata */ }
      finally { setLoadingSubmissions((prev) => ({ ...prev, [assignmentId]: false })); }
    }
  };

  const handleInstructorDownload = (assignmentId, filename) => {
    downloadViaToken(`/instructor/assignments/${assignmentId}/download`, filename);
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Bu ödevi silmek istediğinize emin misiniz? Öğrenci gönderimleri de silinecektir.')) return;
    try {
      const res = await apiFetch(`/instructor/assignments/${assignmentId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
        setExpandedSubmissions((prev) => { const n = { ...prev }; delete n[assignmentId]; return n; });
        setSubmissions((prev) => { const n = { ...prev }; delete n[assignmentId]; return n; });
      }
    } catch { /* sessiz hata */ }
  };

  const handleSubmissionDownload = (assignmentId, submissionId, filename) => {
    downloadViaToken(`/instructor/assignments/${assignmentId}/submissions/${submissionId}/download`, filename);
  };

  const handleStudentDownload = (assignmentId, filename) => {
    downloadViaToken(`/student/assignments/${assignmentId}/download`, filename);
  };

  const handleBulkDownload = (assignmentId, courseCode, courseName, title) => {
    const safe = (s) => (s || '').replace(/[^a-zA-Z0-9À-ž\s\-_]/g, '_').trim();
    const zipName = `${safe(courseCode)}_${safe(courseName)}_${safe(title)}.zip`;
    downloadViaToken(
      `/instructor/assignments/${assignmentId}/submissions/bulk-download`,
      zipName
    );
  };

  const toggleGradeView = async (assignmentId, courseId) => {
    const nowOpen = !gradeView[assignmentId];
    setGradeView((prev) => ({ ...prev, [assignmentId]: nowOpen }));
    if (nowOpen && !gradeStudents[assignmentId]) {
      setLoadingGrades((prev) => ({ ...prev, [assignmentId]: true }));
      setGradeError((prev) => ({ ...prev, [assignmentId]: '' }));
      try {
        const [gradeRes, weightsRes] = await Promise.all([
          apiFetch(`/instructor/assignments/${assignmentId}/grade-list`),
          courseId ? apiFetch(`/courses/${courseId}/exam-weights`) : Promise.resolve(null),
        ]);
        const gradeData = await gradeRes.json();
        if (gradeData.success) {
          setGradeStudents((prev) => ({ ...prev, [assignmentId]: gradeData.students || [] }));
          setDeadlinePassed((prev) => ({ ...prev, [assignmentId]: gradeData.deadline_passed }));
          const inputs = {};
          (gradeData.students || []).forEach((s) => {
            inputs[s.student_no] = s.grade !== null && s.grade !== undefined ? String(s.grade) : '';
          });
          setGradeInputs((prev) => ({ ...prev, [assignmentId]: inputs }));
        } else {
          setGradeError((prev) => ({ ...prev, [assignmentId]: gradeData.message || 'Yüklenemedi' }));
        }
        if (weightsRes) {
          const weightsData = await weightsRes.json();
          if (weightsData.success && weightsData.exam_weights?.length > 0) {
            const weights = weightsData.exam_weights;
            setExamWeights((prev) => ({ ...prev, [assignmentId]: weights }));
            if (!gradeType[assignmentId]) {
              const visible = weights.filter((w) => w.exam_type !== 'Ontest');
              const odevWeight = visible.find((w) =>
                w.exam_type?.toLowerCase().includes('ödev') ||
                w.exam_type?.toLowerCase().includes('odev')
              );
              const defaultWeight = odevWeight || visible[0];
              if (defaultWeight) setGradeType((prev) => ({ ...prev, [assignmentId]: defaultWeight.id }));
            }
          }
        }
      } catch {
        setGradeError((prev) => ({ ...prev, [assignmentId]: 'Bağlantı hatası' }));
      } finally {
        setLoadingGrades((prev) => ({ ...prev, [assignmentId]: false }));
      }
    }
  };

  const handleGradeInputChange = (assignmentId, studentNo, value) => {
    setGradeInputs((prev) => ({
      ...prev,
      [assignmentId]: { ...(prev[assignmentId] || {}), [studentNo]: value },
    }));
  };

  const refreshGradeList = async (assignmentId) => {
    const res = await apiFetch(`/instructor/assignments/${assignmentId}/grade-list`);
    const d = await res.json();
    if (d.success) {
      setGradeStudents((prev) => ({ ...prev, [assignmentId]: d.students || [] }));
      const inputs = {};
      (d.students || []).forEach((s) => {
        inputs[s.student_no] = s.grade !== null && s.grade !== undefined ? String(s.grade) : '';
      });
      setGradeInputs((prev) => ({ ...prev, [assignmentId]: inputs }));
    }
  };

  const handleSaveAllGrades = async (assignmentId) => {
    const inputs = gradeInputs[assignmentId] || {};
    const gradesList = Object.entries(inputs)
      .filter(([, v]) => v !== '')
      .map(([student_no, v]) => ({ student_no, grade: parseFloat(v) }));
    if (gradesList.length === 0) {
      setGradeError((prev) => ({ ...prev, [assignmentId]: 'Hiç not girilmedi.' }));
      return;
    }
    setSavingGrades((prev) => ({ ...prev, [assignmentId]: true }));
    setGradeError((prev) => ({ ...prev, [assignmentId]: '' }));
    setGradeSuccess((prev) => ({ ...prev, [assignmentId]: '' }));
    try {
      const res = await apiFetch(`/instructor/assignments/${assignmentId}/grades/batch`, {
        method: 'POST',
        body: JSON.stringify({ grades: gradesList, exam_weight_id: gradeType[assignmentId] }),
      });
      const data = await res.json();
      if (data.success) {
        setGradeSuccess((prev) => ({
          ...prev,
          [assignmentId]: `${data.saved} not kaydedildi.${data.errors?.length ? ` ${data.errors.length} hata.` : ''}`,
        }));
        await refreshGradeList(assignmentId);
      } else {
        setGradeError((prev) => ({ ...prev, [assignmentId]: data.message || 'Kayıt başarısız' }));
      }
    } catch {
      setGradeError((prev) => ({ ...prev, [assignmentId]: 'Bağlantı hatası' }));
    } finally {
      setSavingGrades((prev) => ({ ...prev, [assignmentId]: false }));
    }
  };

  const handleDownloadGradeExcel = async (assignmentId, title) => {
    setGradeError((prev) => ({ ...prev, [assignmentId]: '' }));
    try {
      const res = await apiFetch(`/instructor/assignments/${assignmentId}/grades/excel`);
      const contentType = res.headers.get('Content-Type') || '';
      if (!res.ok || contentType.includes('application/json')) {
        const data = await res.json();
        setGradeError((prev) => ({ ...prev, [assignmentId]: data.message || 'Excel oluşturulamadı' }));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `NotYuklemeExceli_${title || assignmentId}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setGradeError((prev) => ({ ...prev, [assignmentId]: 'Excel indirilemedi' }));
    }
  };

  const handleUploadGradeExcel = async (assignmentId) => {
    const file = gradeExcelFile[assignmentId];
    if (!file) return;
    setUploadingGradeExcel((prev) => ({ ...prev, [assignmentId]: true }));
    setGradeError((prev) => ({ ...prev, [assignmentId]: '' }));
    setGradeSuccess((prev) => ({ ...prev, [assignmentId]: '' }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (gradeType[assignmentId]) formData.append('exam_weight_id', gradeType[assignmentId]);
      const res = await apiFetch(`/instructor/assignments/${assignmentId}/grades/excel`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setGradeSuccess((prev) => ({ ...prev, [assignmentId]: data.message || 'Notlar yüklendi.' }));
        setGradeExcelFile((prev) => { const n = { ...prev }; delete n[assignmentId]; return n; });
        if (gradeExcelRefs.current[assignmentId]) gradeExcelRefs.current[assignmentId].value = '';
        await refreshGradeList(assignmentId);
      } else {
        setGradeError((prev) => ({ ...prev, [assignmentId]: data.message || 'Yükleme başarısız' }));
      }
    } catch {
      setGradeError((prev) => ({ ...prev, [assignmentId]: 'Bağlantı hatası' }));
    } finally {
      setUploadingGradeExcel((prev) => ({ ...prev, [assignmentId]: false }));
    }
  };

  const downloadViaToken = async (endpoint, filename) => {
    try {
      const res = await apiFetch(endpoint);
      const disposition = res.headers.get('Content-Disposition');
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
        if (match) filename = decodeURIComponent(match[1]);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch { /* sessiz hata */ }
  };

  // Öğrenci: ödev gönder / güncelle
  const handleStudentSubmit = async (assignmentId) => {
    const file = studentFile[assignmentId];
    if (!file) return;
    setSubmitting((prev) => ({ ...prev, [assignmentId]: true }));
    setSubmitError((prev) => ({ ...prev, [assignmentId]: '' }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch(`/student/assignments/${assignmentId}/submit`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setAssignments((prev) =>
          prev.map((a) => a.id === assignmentId ? { ...a, submitted: true } : a)
        );
        setStudentFile((prev) => { const n = { ...prev }; delete n[assignmentId]; return n; });
        if (studentFileRefs.current[assignmentId]) studentFileRefs.current[assignmentId].value = '';
      } else {
        setSubmitError((prev) => ({ ...prev, [assignmentId]: data.message || 'Yükleme başarısız' }));
      }
    } catch {
      setSubmitError((prev) => ({ ...prev, [assignmentId]: 'Bağlantı hatası' }));
    } finally {
      setSubmitting((prev) => ({ ...prev, [assignmentId]: false }));
    }
  };

  const renderStudentActions = (a) => {
    const past = isPastDeadline(a.due_at);

    return (
      <div className="assign-card-actions">
        <button
          className="assign-btn assign-btn-outline"
          onClick={() => handleStudentDownload(a.id, a.original_filename)}
        >
          İndir
        </button>

        {past ? (
          <span className="assign-deadline-past-badge">Süre doldu</span>
        ) : a.submitted ? (
          <>
            <span className="assign-submitted-badge">✓ Gönderildi</span>
            <input
              type="file"
              accept=".pdf,.pptx,.docx"
              className="assign-file-input"
              ref={(el) => { studentFileRefs.current[a.id] = el; }}
              onChange={(e) => setStudentFile((prev) => ({ ...prev, [a.id]: e.target.files[0] || null }))}
            />
            <button
              className="assign-btn assign-btn-success"
              disabled={!studentFile[a.id] || submitting[a.id]}
              onClick={() => handleStudentSubmit(a.id)}
            >
              {submitting[a.id] ? 'Yükleniyor...' : 'Güncelle'}
            </button>
            {submitError[a.id] && (
              <span style={{ fontSize: '0.78rem', color: '#b91c1c' }}>{submitError[a.id]}</span>
            )}
          </>
        ) : (
          <>
            <input
              type="file"
              accept=".pdf,.pptx,.docx"
              className="assign-file-input"
              ref={(el) => { studentFileRefs.current[a.id] = el; }}
              onChange={(e) => setStudentFile((prev) => ({ ...prev, [a.id]: e.target.files[0] || null }))}
            />
            <button
              className="assign-btn assign-btn-success"
              disabled={!studentFile[a.id] || submitting[a.id]}
              onClick={() => handleStudentSubmit(a.id)}
            >
              {submitting[a.id] ? 'Yükleniyor...' : 'Gönder'}
            </button>
            {submitError[a.id] && (
              <span style={{ fontSize: '0.78rem', color: '#b91c1c' }}>{submitError[a.id]}</span>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <h2 className="au-title">Ödevler</h2>
          {isInstructor && selectedCourseId && (
            <button className="assign-upload-btn" onClick={openModal}>
              + Yeni Ödev Yükle
            </button>
          )}
        </div>
      </div>

      {/* Aktif ders çubuğu olmayan ekranlarda ders seçimi burada yapılır. */}
      {!activeCourseContext && (
        <div className="assign-selector-panel">
          <CourseDropdown
            courses={courses}
            selectedCourseId={selectedCourseId}
            onChange={handleCourseChange}
            loading={loadingCourses}
          />
        </div>
      )}

      {error && (
        <div className="assign-error">
          <span>{error}</span>
          <button
            className="assign-retry-btn"
            onClick={() => isInstructor
              ? (selectedCourseId ? fetchAssignments(selectedCourseId) : fetchAllInstructorAssignments())
              : fetchAssignments(selectedCourseId || undefined)
            }
          >
            Tekrar Dene
          </button>
        </div>
      )}


      {!error && loadingAssignments && (
        <div className="assign-loading">
          <div className="assign-spinner" />
          <p>Yükleniyor...</p>
        </div>
      )}

      {!error && !loadingAssignments && assignments.length === 0 && (!isInstructor || selectedCourseId) && (
        <div className="assign-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          <p className="assign-empty-text">
            {selectedCourseId
              ? 'Bu derse ait henüz ödev eklenmemiş.'
              : 'Henüz hiçbir derse ödev eklenmemiş.'}
          </p>
        </div>
      )}

      {!error && assignments.length > 0 && (
        <div className="assign-list">
          {assignments.map((a) => (
            <div key={a.id} className="assign-card">
              <div className="assign-card-header">
                <div className="assign-card-info">
                  <p className="assign-card-title">{a.title}</p>
                  <div className="assign-card-meta">
                    <span>{a.course_code ? `${a.course_code} – ${a.course_name}` : a.course_name}</span>
                    {a.created_at && (
                      <span>
                        {new Date(a.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric'
                        })}
                      </span>
                    )}
                    <span>{a.original_filename}</span>
                  </div>
                  {/* Deadline gösterimi — hem akademisyen hem öğrenci görür */}
                  {a.due_at && (
                    <div className={isPastDeadline(a.due_at) ? 'assign-deadline assign-deadline-past' : 'assign-deadline'}>
                      Son teslim: {formatDeadline(a.due_at)}
                      {isPastDeadline(a.due_at) && <span className="assign-deadline-tag">Süresi doldu</span>}
                    </div>
                  )}
                  {/* Not gösterimi — sadece öğrenci */}
                  {!isInstructor && a.grade !== null && a.grade !== undefined && (
                    <div className="assign-student-grade">
                      <span className="assign-student-grade-label">Notunuz</span>
                      <span className="assign-student-grade-value">{a.grade}</span>
                      <span className="assign-student-grade-max">/ 100</span>
                    </div>
                  )}
                </div>

                {isInstructor ? (
                  <div className="assign-card-actions">
                    <button
                      className="assign-btn assign-btn-outline"
                      onClick={() => handleInstructorDownload(a.id, a.original_filename)}
                    >
                      İndir
                    </button>
                    <button
                      className="assign-btn assign-btn-primary"
                      onClick={() => toggleSubmissions(a.id)}
                    >
                      {expandedSubmissions[a.id] ? 'Gönderimleri Gizle' : 'Gönderimleri Gör'}
                    </button>
                    <button
                      className="assign-btn assign-btn-outline"
                      onClick={() => toggleGradeView(a.id, a.course_id)}
                    >
                      {gradeView[a.id] ? 'Notları Gizle' : 'Not Gir'}
                    </button>
                    <button
                      className="assign-btn assign-btn-danger"
                      onClick={() => handleDeleteAssignment(a.id)}
                    >
                      Sil
                    </button>
                  </div>
                ) : (
                  renderStudentActions(a)
                )}
              </div>

              {/* Akademisyen: öğrenci gönderimleri */}
              {isInstructor && expandedSubmissions[a.id] && (
                <div className="assign-submissions">
                  <div className="assign-submissions-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Öğrenci Gönderimleri</span>
                    {submissions[a.id] && submissions[a.id].length > 0 && (
                      <button
                        className="assign-btn assign-btn-outline"
                        onClick={() => handleBulkDownload(a.id, a.course_code, a.course_name, a.title)}
                        title="Tüm gönderimleri ZIP olarak indir"
                      >
                        Toplu İndir
                      </button>
                    )}
                  </div>
                  {loadingSubmissions[a.id] ? (
                    <div className="assign-loading" style={{ padding: '0.75rem 0' }}>
                      <div className="assign-spinner" style={{ width: 20, height: 20 }} />
                    </div>
                  ) : !submissions[a.id] || submissions[a.id].length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Henüz gönderim yok.</p>
                  ) : (
                    submissions[a.id].map((s) => (
                      <div key={s.id} className="assign-submission-row">
                        <div>
                          <div className="assign-submission-student">
                            {s.student_name} ({s.student_number})
                          </div>
                          <div className="assign-submission-meta">
                            {s.original_filename} &middot;{' '}
                            {s.submitted_at && new Date(s.submitted_at).toLocaleString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <button
                          className="assign-btn assign-btn-outline"
                          onClick={() => handleSubmissionDownload(a.id, s.id, s.original_filename)}
                        >
                          İndir
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Not girişi paneli */}
              {isInstructor && gradeView[a.id] && (
                <div className="assign-grade-panel">
                  <div className="assign-grade-panel-header">
                    <span className="assign-submissions-title">Not Girişi</span>
                    <div className="assign-grade-controls">
                      <div className="assign-grade-type-group">
                        <label className="assign-modal-label">Sınav Türü</label>
                        <select
                          className="assign-selector-select"
                          value={gradeType[a.id] || ''}
                          onChange={(e) =>
                            setGradeType((prev) => ({ ...prev, [a.id]: Number(e.target.value) }))
                          }
                        >
                          {(examWeights[a.id] || [])
                            .filter((w) => w.exam_type !== 'Ontest')
                            .map((w) => (
                              <option key={w.id} value={w.id}>{getExamTypeLabel(w.exam_type)}</option>
                            ))}
                          {(!examWeights[a.id] || examWeights[a.id].filter((w) => w.exam_type !== 'Ontest').length === 0) && (
                            <option value="">Yükleniyor...</option>
                          )}
                        </select>
                      </div>
                      <button
                        className="assign-btn assign-btn-outline"
                        onClick={() => handleDownloadGradeExcel(a.id, a.title)}
                        title="Öğrenci listesiyle dolu şablonu indir"
                      >
                        Şablon İndir
                      </button>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        className="assign-file-input"
                        ref={(el) => { gradeExcelRefs.current[a.id] = el; }}
                        onChange={(e) =>
                          setGradeExcelFile((prev) => ({ ...prev, [a.id]: e.target.files[0] || null }))
                        }
                      />
                      <button
                        className="assign-btn assign-btn-success"
                        disabled={!gradeExcelFile[a.id] || uploadingGradeExcel[a.id] || deadlinePassed[a.id] === false}
                        onClick={() => handleUploadGradeExcel(a.id)}
                      >
                        {uploadingGradeExcel[a.id] ? 'Yükleniyor...' : 'Excel ile Yükle'}
                      </button>
                    </div>
                  </div>

                  {deadlinePassed[a.id] === false && (
                    <div className="assign-grade-deadline-warning">
                      ⚠ Son teslim tarihi henüz geçmedi. Not girişi teslim süresi dolduktan sonra yapılabilir.
                    </div>
                  )}
                  {gradeError[a.id] && (
                    <p style={{ color: '#b91c1c', fontSize: '0.82rem', margin: '0.3rem 0' }}>
                      {gradeError[a.id]}
                    </p>
                  )}
                  {gradeSuccess[a.id] && (
                    <div className="assign-grade-success-banner">
                      <span className="assign-grade-success-icon">✓</span>
                      {gradeSuccess[a.id]}
                    </div>
                  )}

                  {loadingGrades[a.id] ? (
                    <div className="assign-loading" style={{ padding: '0.75rem 0' }}>
                      <div className="assign-spinner" style={{ width: 20, height: 20 }} />
                    </div>
                  ) : !gradeStudents[a.id] || gradeStudents[a.id].length === 0 ? (
                    <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                      Bu derse kayıtlı öğrenci bulunamadı.
                    </p>
                  ) : (
                    <>
                      <div className="assign-grade-table">
                        <div className="assign-grade-table-head">
                          <span>Öğrenci No</span>
                          <span>Ad Soyad</span>
                          <span>Teslim</span>
                          <span>Not (0–100)</span>
                        </div>
                        {gradeStudents[a.id].map((s) => (
                          <div key={s.student_no} className="assign-grade-table-row">
                            <span className="assign-submission-student">{s.student_no}</span>
                            <span style={{ fontSize: '0.85rem', color: '#334155' }}>{s.student_name}</span>
                            <span>
                              {s.has_submission
                                ? <span className="assign-submitted-badge" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}>Teslim Etti</span>
                                : <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>Teslim Etmedi</span>
                              }
                            </span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              className="assign-grade-input"
                              placeholder={s.has_submission ? '—' : '0'}
                              disabled={!s.has_submission}
                              value={(gradeInputs[a.id] || {})[s.student_no] ?? ''}
                              onChange={(e) => handleGradeInputChange(a.id, s.student_no, e.target.value)}
                              title={!s.has_submission ? 'Ödevi teslim etmedi' : ''}
                            />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                        <button
                          className="assign-btn assign-btn-primary"
                          disabled={savingGrades[a.id] || !gradeType[a.id] || deadlinePassed[a.id] === false}
                          onClick={() => handleSaveAllGrades(a.id)}
                        >
                          {savingGrades[a.id] ? 'Kaydediliyor...' : 'Notları Kaydet'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Akademisyen yükleme modal */}
      {showModal && (
        <div className="assign-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="assign-modal">
            <p className="assign-modal-title">Yeni Ödev Yükle</p>
            <div className="assign-modal-field">
              <label className="assign-modal-label">Başlık</label>
              <input
                type="text"
                className="assign-modal-input"
                placeholder="Ödev başlığı"
                value={modalTitle}
                onChange={(e) => setModalTitle(e.target.value)}
              />
            </div>
            <div className="assign-modal-field">
              <label className="assign-modal-label">Son Teslim Tarihi ve Saati</label>
              <input
                type="datetime-local"
                className="assign-modal-input"
                value={modalDueAt}
                onChange={(e) => setModalDueAt(e.target.value)}
              />
              <span className="assign-modal-hint">Boş bırakılırsa süre sınırı uygulanmaz</span>
            </div>
            <div className="assign-modal-field">
              <label className="assign-modal-label">Dosya</label>
              <input
                type="file"
                accept=".pdf,.pptx,.docx"
                ref={modalFileRef}
                onChange={(e) => setModalFile(e.target.files[0] || null)}
              />
              <span className="assign-modal-hint">PDF, PPTX veya DOCX — maks. 50 MB</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.5rem', padding: '0.6rem 0.75rem', margin: '0.25rem 0' }}>
              <span style={{ fontSize: '0.8rem', color: '#92400e' }}>
                Ödev yüklendiği anda derse kayıtlı tüm öğrencilere e-posta bildirimi gönderilecektir.
              </span>
            </div>
            {modalError && (
              <p style={{ color: '#b91c1c', fontSize: '0.84rem', margin: '0.25rem 0' }}>{modalError}</p>
            )}
            <div className="assign-modal-actions">
              <button className="assign-modal-cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button
                className="assign-btn assign-btn-primary"
                disabled={uploading}
                onClick={handleModalUpload}
              >
                {uploading ? 'Yükleniyor...' : 'Yükle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Assignments;
